import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, Select, SimpleGrid, Spinner, Badge,
  Stat, StatLabel, StatNumber, Icon,
} from '@chakra-ui/react';
import { FiRefreshCw, FiTrendingUp, FiActivity, FiBox } from 'react-icons/fi';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from 'recharts';

const API_BASE = '/api';
const COLORS = ['#0891B2', '#6366F1', '#F59E0B', '#EF4444', '#10B981', '#8B5CF6', '#F97316', '#06B6D4'];

function UsageAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  const fetchAnalytics = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/analytics/?days=${days}`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [days]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${d.getDate()}/${d.getMonth() + 1}`;
  };

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="cyan.500" /></Box>;
  if (!data) return <Text color="gray.500" textAlign="center" py={20}>Failed to load analytics</Text>;

  const totalAuditEvents = (data.audit_by_action || []).reduce((s, a) => s + a.count, 0);
  const totalNewVersions = (data.versions_by_day || []).reduce((s, a) => s + a.count, 0);

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Usage Analytics</Heading>
          <Text color="gray.500">Activity trends and usage patterns</Text>
        </VStack>
        <HStack>
          <Select value={days} onChange={(e) => setDays(Number(e.target.value))} w="140px" bg="white">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </Select>
          <Button leftIcon={<FiRefreshCw />} colorScheme="cyan" onClick={fetchAnalytics}>Refresh</Button>
        </HStack>
      </HStack>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={8}>
        <Stat bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack>
            <Box p={3} bg="cyan.100" borderRadius="lg">
              <Icon as={FiActivity} boxSize={6} color="cyan.600" />
            </Box>
            <VStack align="start" spacing={0}>
              <StatLabel color="gray.500">Audit Events</StatLabel>
              <StatNumber fontSize="2xl">{totalAuditEvents}</StatNumber>
            </VStack>
          </HStack>
        </Stat>
        <Stat bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack>
            <Box p={3} bg="purple.100" borderRadius="lg">
              <Icon as={FiTrendingUp} boxSize={6} color="purple.600" />
            </Box>
            <VStack align="start" spacing={0}>
              <StatLabel color="gray.500">New Versions</StatLabel>
              <StatNumber fontSize="2xl">{totalNewVersions}</StatNumber>
            </VStack>
          </HStack>
        </Stat>
        <Stat bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack>
            <Box p={3} bg="blue.100" borderRadius="lg">
              <Icon as={FiBox} boxSize={6} color="blue.600" />
            </Box>
            <VStack align="start" spacing={0}>
              <StatLabel color="gray.500">Total Applications</StatLabel>
              <StatNumber fontSize="2xl">{data.total_applications || 0}</StatNumber>
            </VStack>
          </HStack>
        </Stat>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Heading size="sm" mb={4}>Audit Activity</Heading>
          {data.audit_by_day?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={data.audit_by_day}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={formatDate} fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip labelFormatter={(v) => new Date(v).toLocaleDateString()} />
                <Area type="monotone" dataKey="count" stroke="#0891B2" fill="#0891B2" fillOpacity={0.2} name="Events" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <Text color="gray.400" textAlign="center" py={10}>No activity data</Text>}
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Heading size="sm" mb={4}>Versions Published</Heading>
          {data.versions_by_day?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.versions_by_day}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={formatDate} fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip labelFormatter={(v) => new Date(v).toLocaleDateString()} />
                <Bar dataKey="count" fill="#6366F1" name="Versions" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <Text color="gray.400" textAlign="center" py={10}>No version data</Text>}
        </Box>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6} mb={6}>
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Heading size="sm" mb={4}>Actions Breakdown</Heading>
          {data.audit_by_action?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={data.audit_by_action} dataKey="count" nameKey="action" cx="50%" cy="50%" outerRadius={80} label={({action, count}) => `${action} (${count})`}>
                  {data.audit_by_action.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <Text color="gray.400" textAlign="center" py={10}>No data</Text>}
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Heading size="sm" mb={4}>Application Status</Heading>
          <VStack align="stretch" spacing={2}>
            {(data.app_statuses || []).map((s, i) => (
              <HStack key={i} justify="space-between">
                <Badge colorScheme={s.status === 'active' ? 'green' : 'gray'} fontSize="sm">
                  {s.status}
                </Badge>
                <Text fontWeight="bold">{s.count}</Text>
              </HStack>
            ))}
            {(!data.app_statuses || data.app_statuses.length === 0) && (
              <Text color="gray.400" textAlign="center" py={4}>No applications</Text>
            )}
          </VStack>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Heading size="sm" mb={4}>Version Status</Heading>
          <VStack align="stretch" spacing={2}>
            {(data.version_statuses || []).map((s, i) => (
              <HStack key={i} justify="space-between">
                <Badge colorScheme={s.status === 'stable' ? 'green' : s.status === 'deprecated' ? 'red' : 'yellow'} fontSize="sm">
                  {s.status}
                </Badge>
                <Text fontWeight="bold">{s.count}</Text>
              </HStack>
            ))}
            {(!data.version_statuses || data.version_statuses.length === 0) && (
              <Text color="gray.400" textAlign="center" py={4}>No versions</Text>
            )}
          </VStack>
        </Box>
      </SimpleGrid>
    </Box>
  );
}

export default UsageAnalytics;
